import { useCallback, useEffect, useState } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { doc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { toast } from "utils/toast";
import { Modal } from "components/modal/Modal";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { FolderSearchIcon } from "lucide-react";
import { ProjectType } from "reedy-data/models";
import {
  BULK_BOOKS_LIMIT,
  SAVE_AS_PROJECT_BOOKS_WARNING_LIMIT,
} from "shared/constants";
import { SearchProductWithScore } from "hooks/search/types";
import { useProjects } from "pages/Projects/contexts/ProjectsContext";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Text } from "components/text";
import { newProjectNavigation } from "~/pages/Projects/newProjectNavigation";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { AddToProjectModal } from "./AddToProjectModal";
import { getProjectsCollection } from "../../../integrations/firebase/collections";

interface AddToProjectDropdownItemProps {
  isSelectedAll?: boolean;
  selectedBooksCount: number;
  selectedIds: string[];
  allBooksCount: number;
  fetchAllResults: () => Promise<SearchProductWithScore[]>;
}

export const AddToProjectDropdownItem = ({
  isSelectedAll,
  selectedBooksCount,
  selectedIds,
  allBooksCount,
  fetchAllResults,
}: AddToProjectDropdownItemProps) => {
  const { createProject, updateProjectIds, errorCreate } = useProjects();
  const { t } = useTranslation(["books", "general"]);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isAddToProjectOpen, setIsAddToProjectOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [destinationProjectId, setDestinationProjectId] = useState<
    string | null
  >(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [destinationProject] = useDocumentData(
    destinationProjectId
      ? doc(getProjectsCollection(), destinationProjectId)
      : undefined,
  );

  useEffect(() => {
    if (errorCreate) {
      toast.error(errorCreate.message);
    }
  }, [errorCreate]);

  const isLimitReached = selectedBooksCount > BULK_BOOKS_LIMIT;
  const isWarningReached =
    selectedBooksCount > SAVE_AS_PROJECT_BOOKS_WARNING_LIMIT && !isLimitReached;

  const handleCreateOrUpdateProject = useCallback(async () => {
    let ids = selectedIds;

    if (isSelectedAll && selectedIds.length < allBooksCount) {
      setIsLoading(true);
      const products = await fetchAllResults();
      ids = products.map((p) => p.id);
    }

    if (destinationProject && destinationProject.id) {
      updateProjectIds(destinationProject.id, [
        ...(destinationProject.productIds || []),
        ...ids,
      ]);
      toast.success(
        t("books:addToProject.success", {
          count: ids.length,
          project: destinationProject?.name || "",
        }),
      );
    } else {
      const newProject = await createProject(
        t("general:newProjectName"),
        ids,
        ProjectType.ISBNS,
      );
      if (newProject) {
        navigate(newProjectNavigation(newProject, location.href));
      }
    }

    setIsConfirmationOpen(false);
    setIsLoading(false);
  }, [
    destinationProject,
    updateProjectIds,
    navigate,
    t,
    fetchAllResults,
    createProject,
    location,
    isSelectedAll,
    selectedIds,
    allBooksCount,
  ]);

  return (
    <>
      <AddToProjectModal
        open={isAddToProjectOpen}
        onOpenChange={() => setIsAddToProjectOpen(false)}
        selectedBooksCount={selectedBooksCount}
        onCreateProject={() => {
          setDestinationProjectId(null);
          setIsAddToProjectOpen(false);
          setIsConfirmationOpen(true);
        }}
        onSelectProject={(projectId) => {
          setDestinationProjectId(projectId);
          setIsAddToProjectOpen(false);
          setIsConfirmationOpen(true);
        }}
      />
      <Modal
        id="confirm-project"
        loading={isLoading}
        open={isConfirmationOpen}
        variant="warning"
        title={t("books:addToProject.confirmation.header")}
        description={
          destinationProjectId
            ? t("books:addToProject.confirmation.update.description", {
                count: selectedBooksCount,
                project: destinationProject?.name || "",
              })
            : t("books:addToProject.confirmation.create.description", {
                count: selectedBooksCount,
              })
        }
        confirmLabel={t("books:addToProject.confirmation.createProject")}
        cancelLabel={t("general:buttons.cancel")}
        onOpenChange={() => {
          setIsConfirmationOpen(false);
        }}
        onConfirm={() => handleCreateOrUpdateProject()}
      />
      <Modal
        id="save-project"
        open={isWarningOpen}
        variant="warning"
        title={t("books:selectedBooks.saveProjectWarningModal.header")}
        description={t(
          "books:selectedBooks.saveProjectWarningModal.description",
          { count: SAVE_AS_PROJECT_BOOKS_WARNING_LIMIT },
        )}
        confirmLabel={t("books:selectedBooks.saveAsProject")}
        cancelLabel={t("general:buttons.cancel")}
        onOpenChange={() => setIsWarningOpen(false)}
        onConfirm={() => {
          setIsWarningOpen(false);
          setIsAddToProjectOpen(true);
        }}
      />
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <span>
            <DropdownMenuItem
              className="py-2.5 px-3 cursor-pointer"
              disabled={isLimitReached}
              onClick={(event) => {
                event.preventDefault();
                return isWarningReached
                  ? setIsWarningOpen(true)
                  : setIsAddToProjectOpen(true);
              }}
              role="menuitem"
            >
              <Text as="span" variant="body" className="flex items-center">
                <FolderSearchIcon className="mr-2 h-4 w-4" />
                {t("books:dropdown.takeAction.item.addToProject")}
              </Text>
            </DropdownMenuItem>
          </span>
        </TooltipTrigger>
        {isLimitReached ? (
          <TooltipContent
            side="bottom"
            className="font-light text-center shadow-tooltip text-secondary-600 whitespace-pre px-5 z-10 border-none"
            sideOffset={3}
            align="center"
          >
            <TooltipArrow
              className="fill-white drop-shadow-xs"
              width={12}
              height={6}
            />
            <p className="whitespace-pre">
              {t("books:selectedBooks.limit", {
                count: BULK_BOOKS_LIMIT,
              })}
            </p>
          </TooltipContent>
        ) : null}
      </Tooltip>
    </>
  );
};
